"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeatureControlLimitChart = exports.Chart = void 0;
/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const fp_1 = require("lodash/fp");
const narwhal_1 = require("@centralwebteam/narwhal");
const colours_1 = require("../colours");
const styled_components_1 = require("../styled-components");
const __1 = require("../");
const EmptyState_1 = require("../EmptyState");
const { isNumber } = narwhal_1.checks;
const Chart = ({ outerProcessLimit = 0, upperTolerance, lowerTolerance, controlLimit = 0, innerNullBand = 0, processTarget, nominal, results, }) => {
    const { t } = (0, react_i18next_1.useTranslation)("central-web-app-components");
    if (typeof processTarget !== "number" || isNaN(processTarget)) {
        return react_1.default.createElement(EmptyState_1.EmptyState, { text: "message-NoProcessTarget" });
    }
    /* Limit / label positions, with limit names in conventional order
  
                     0
    │                0.5  Bin 0
    ├─────────────── 1    Outer process limit
    │                1.5  Bin 1
    ├─────────────── 2    Upper Control Limit
    │                2.5  Bin 2
    ├─────────────── 3    Inner Null Band
    │                3.5  Bin 3
    ├─────────────── 4    Process Target
    │                4.5  Bin 4
    ├─────────────── 5    Inner Null Band
    │                5.5  Bin 5
    ├─────────────── 6    Lower Control Limit
    │                6.5  Bin 6
    ├─────────────── 7    Lower Process Limit
    │                7.5  Bin 7
                     8
  */
    /** Information on how to display the process limits */
    const limits = [
        {
            text: t("label-Outer Process Limit"),
            colour: colours_1.colours.Fail,
            value: processTarget + outerProcessLimit,
            relativeValue: outerProcessLimit,
        },
        {
            text: t("label-Upper Control Limit"),
            colour: colours_1.colours.Warning,
            value: processTarget + controlLimit,
            relativeValue: controlLimit,
        },
        {
            text: t("label-Inner Null Band"),
            colour: colours_1.colours.Pass,
            value: processTarget + innerNullBand,
            relativeValue: innerNullBand,
        },
        {
            text: t("label-Process Target"),
            colour: colours_1.colours.Nominal,
            value: processTarget,
            relativeValue: 0,
        },
        {
            text: t("label-Inner Null Band"),
            colour: colours_1.colours.Pass,
            value: processTarget - innerNullBand,
            relativeValue: 0 - innerNullBand,
        },
        {
            text: t("label-Lower Control Limit"),
            colour: colours_1.colours.Warning,
            value: processTarget - controlLimit,
            relativeValue: 0 - controlLimit,
        },
        {
            text: t("label-Outer Process Limit"),
            colour: colours_1.colours.Fail,
            value: processTarget - outerProcessLimit,
            relativeValue: 0 - outerProcessLimit,
        },
    ].sort((a, b) => b.value - a.value);
    /** Sort a deviation into a band (0-7 from highest to lowest) */
    const getBand = (value) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        return isNumber((_a = limits[0]) === null || _a === void 0 ? void 0 : _a.relativeValue) && value > limits[0].relativeValue
            ? 0
            : isNumber((_b = limits[1]) === null || _b === void 0 ? void 0 : _b.relativeValue) && value > limits[1].relativeValue
                ? 1
                : isNumber((_c = limits[2]) === null || _c === void 0 ? void 0 : _c.relativeValue) && value > limits[2].relativeValue
                    ? 2
                    : value > 0
                        ? 3
                        : isNumber((_d = limits[6]) === null || _d === void 0 ? void 0 : _d.relativeValue) &&
                            value < ((_e = limits[6]) === null || _e === void 0 ? void 0 : _e.relativeValue)
                            ? 7
                            : isNumber((_f = limits[5]) === null || _f === void 0 ? void 0 : _f.relativeValue) &&
                                value < ((_g = limits[5]) === null || _g === void 0 ? void 0 : _g.relativeValue)
                                ? 6
                                : isNumber((_h = limits[4]) === null || _h === void 0 ? void 0 : _h.relativeValue) &&
                                    value < ((_j = limits[4]) === null || _j === void 0 ? void 0 : _j.relativeValue)
                                    ? 5
                                    : 4;
    };
    /** Histogram bins (number of results per band, top to bottom) */
    const bins = [0, 0, 0, 0, 0, 0, 0, 0];
    results === null || results === void 0 ? void 0 : results.forEach((r) => {
        const deviationFromTarget = r.actual - processTarget;
        bins[getBand(deviationFromTarget)]++;
    });
    /** Position for a tolerance line (0-7 from highest to lowest). x.5 numbers are mid-band, whole numbers are exactly on the band limits. */
    const getTolerancePosition = (value) => {
        switch (value) {
            // Out of order, to prioritise inner limits if they are the same
            case limits[3].relativeValue:
                return 4;
            case limits[2].relativeValue:
                return 3;
            case limits[1].relativeValue:
                return 2;
            case limits[0].relativeValue:
                return 1;
            case limits[4].relativeValue:
                return 5;
            case limits[5].relativeValue:
                return 6;
            case limits[6].relativeValue:
                return 7;
            default:
                return getBand(value) + 0.5;
        }
    };
    /** Information on how to display the upper and lower tolerances and the nominal value */
    const tolerances = [];
    if (isNumber(nominal)) {
        const nominalOffset = nominal - processTarget;
        tolerances.push({
            text: t("label-Nominal"),
            colour: colours_1.colours.Nominal,
            value: nominal,
            relativeValue: nominalOffset,
            position: getTolerancePosition(nominalOffset),
            labelPosition: 1,
            dashed: nominal !== processTarget,
        });
        if (isNumber(upperTolerance)) {
            tolerances.push({
                text: t("label-Upper Tolerance"),
                colour: colours_1.colours.Fail,
                value: nominal + upperTolerance,
                relativeValue: nominalOffset + upperTolerance,
                position: getTolerancePosition(nominalOffset + upperTolerance),
                labelPosition: 4,
            });
        }
        if (isNumber(lowerTolerance)) {
            tolerances.push({
                text: t("label-Lower Tolerance"),
                colour: colours_1.colours.Fail,
                value: nominal + lowerTolerance,
                relativeValue: nominalOffset + lowerTolerance,
                position: getTolerancePosition(nominalOffset + lowerTolerance),
                labelPosition: 7,
            });
        }
    }
    tolerances.sort((a, b) => b.value - a.value);
    // Change label position to avoid collisions, if necessary
    if (tolerances.length > 1) {
        const labelPositions = tolerances.map((t) => t.position);
        if (labelPositions[1] - labelPositions[0] < 1) {
            // Labels 0 and 1 colliding
            if (labelPositions[0] < 1.5) {
                // collision is at top of graphic
                labelPositions[0] = 0.5;
                labelPositions[1] = 1.5;
                if (tolerances.length > 2 && labelPositions[2] < 2.5) {
                    labelPositions[2] = 2.5;
                }
            }
            else {
                // there is space to move label 0 up
                labelPositions[0]--;
            }
        }
        if (tolerances.length > 2 && labelPositions[2] - labelPositions[1] < 1) {
            // Labels 1 and 2 colliding
            if (labelPositions[2] > 6.5) {
                // collision is at bottom of graphic
                labelPositions[1] = 6.5;
                labelPositions[2] = 7.5;
                if (labelPositions[0] > 5.5) {
                    // all three labels colliding
                    labelPositions[0] = 5.5;
                }
            }
            else {
                // there is space to move label 2 down
                labelPositions[2]++;
            }
        }
        // Copy label positions into tolerances
        labelPositions.forEach((p, i) => {
            tolerances[i].labelPosition = p;
        });
    }
    /** Colours for the bands, corrected for the limit order.
     * Any bands outside outer process limits are red, then any outside control limits are pink, and so on. */
    const bandColours = limits.map((limit) => {
        if (limit.relativeValue >= outerProcessLimit ||
            limit.relativeValue < 0 - outerProcessLimit)
            return colours_1.colours.OuterLimit;
        if (limit.relativeValue >= controlLimit ||
            limit.relativeValue < 0 - controlLimit)
            return colours_1.colours.Fail;
        if (limit.relativeValue >= innerNullBand ||
            limit.relativeValue < 0 - innerNullBand)
            return colours_1.colours.Warning;
        return colours_1.colours.Pass;
    });
    // There are only 7 limits, so fill in the 8th band (always red)
    bandColours.push(colours_1.colours.OuterLimit);
    const bands = results
        ? bins.map((b, i) => {
            const percent = Math.round((b / results.length) * 100);
            return {
                count: b,
                colour: bandColours[i],
                title: t("label-resultCount", {
                    count: b,
                    percent,
                }),
            };
        })
        : [];
    return (react_1.default.createElement(Grid, null,
        react_1.default.createElement(__1.Flex, { noWrap: true, col: true, flex: "1 0 auto", justify: "space-evenly" },
            react_1.default.createElement(LimitTextSpacer, { key: "top-spacer" }),
            limits.map((l, i) => (react_1.default.createElement(LimitText, { key: i },
                react_1.default.createElement(__1.Text, null, l.text),
                react_1.default.createElement(__1.Text, null,
                    react_1.default.createElement(__1.NumericValue, { value: l.value, name: l.text }))))),
            react_1.default.createElement(LimitTextSpacer, { key: "bottom-spacer" })),
        react_1.default.createElement(YAxis, null),
        react_1.default.createElement(Graphic, { limits: limits, bands: bands }),
        react_1.default.createElement(__1.FlexSpace, null),
        react_1.default.createElement(ToleranceLines, { tolerances: tolerances }),
        react_1.default.createElement(ToleranceTextColumn, null, tolerances.map((t, i) => (react_1.default.createElement(react_1.default.Fragment, { key: i },
            react_1.default.createElement(Spacer, { "aria-hidden": true }, t.text),
            react_1.default.createElement(ToleranceText, { style: { top: `${t.labelPosition * 12.5}%` } },
                react_1.default.createElement(__1.Text, null, t.text),
                react_1.default.createElement(__1.Text, null,
                    react_1.default.createElement(__1.NumericValue, { value: t.value, name: t.text })))))))));
};
exports.Chart = Chart;
const Grid = styled_components_1.styled.figure `
  display: grid;
  grid-template-columns: auto 10px 1fr 10px 30px auto;
  align-items: stretch;
  margin: 10px 0;
  height: 320px;
  min-width: 420px;
`;
const LimitText = styled_components_1.styled.div `
  display: flex;
  flex: 1 1 12.5%;
  flex-direction: column;
  align-items: end;
  justify-content: center;
  margin: 0 10px;
`;
const LimitTextSpacer = styled_components_1.styled.div `
  flex: 1 1 6.25%;
`;
const ToleranceTextColumn = styled_components_1.styled.div `
  flex: 1 1 20%;
  position: relative;
`;
const ToleranceText = styled_components_1.styled.div `
  transform: translateY(-50%);
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 10px;
`;
/** These hidden elements are required to give the tolerance text column its proper width;
 * the displayed elements are absolutely positioned, and so do not contribute to the auto width of the column.*/
const Spacer = styled_components_1.styled.div `
  margin: 10px;
  visibility: hidden;
`;
/** Vertical axis with 7 evenly spaced ticks */
const YAxis = () => {
    const theme = (0, styled_components_1.useTheme)();
    return (react_1.default.createElement("svg", { viewBox: "0 0 10 8", preserveAspectRatio: "none", style: {
            stroke: theme.fg,
            width: "100%",
        } },
        [1, 2, 3, 4, 5, 6, 7].map((n) => (react_1.default.createElement("line", { key: `tick${n}`, x2: "5", y1: n, y2: n, vectorEffect: "non-scaling-stroke" }))),
        react_1.default.createElement("line", { key: "vertical", x1: "8", x2: "8", y2: "8", vectorEffect: "non-scaling-stroke" })));
};
/** Markers for the tolerances and nominal */
const ToleranceLines = ({ tolerances }) => {
    return (react_1.default.createElement("svg", { viewBox: "0 0 8 8", preserveAspectRatio: "none", style: { flex: "0 1 40px", width: "100%" } }, tolerances.map((t) => {
        return (react_1.default.createElement("path", { key: t.text, stroke: t.colour, fill: "none", vectorEffect: "non-scaling-stroke", strokeLinejoin: "round", strokeDasharray: t.dashed ? "4 2.25" : undefined, d: `M0,${t.position} 4,${t.position} 4,${t.labelPosition} 8,${t.labelPosition}` }));
    })));
};
/** SVG chart with 8 horizontal bars */
const Graphic = ({ limits, bands }) => {
    const theme = (0, styled_components_1.useTheme)();
    // default / empty state
    const zeroes = [0, 0, 0, 0, 0, 0, 0, 0];
    const maxCount = Math.max(...bands.map((b) => b.count));
    const widths = maxCount
        ? bands.map((b) => (b.count / maxCount) * 100)
        : zeroes;
    // Animation end points (copied from new values)
    const [endWidths, setEndWidths] = react_1.default.useState(zeroes);
    // Animation start points (previous values)
    const [startWidths, setStartWidths] = react_1.default.useState(zeroes);
    // Timeout ref to cancel if a second timeout is started
    const [currentTimeout, setCurrentTimeout] = react_1.default.useState(undefined);
    const [currentAnimations, setCurrentAnimations] = react_1.default.useState("");
    // Animation duration 1 second if starting from 0, 0.5 second otherwise
    const duration = startWidths.some((w) => w) ? 0.5 : 1;
    if (!(0, fp_1.isEqual)(widths)(endWidths)) {
        // This causes a re-render with the processed values in state
        setEndWidths(widths);
        setStartWidths(endWidths);
        // After the animation has finished, set the start to the end value to prevent repeated animation
        if (currentTimeout) {
            clearTimeout(currentTimeout);
        }
        setCurrentTimeout(setTimeout(() => {
            setStartWidths(widths);
        }, duration * 1020));
    }
    const animations = bands
        .map((_band, i) => {
        const start = startWidths[i], end = endWidths[i];
        if (start === end)
            return "";
        // We need a new ID on change so that the animation restarts
        const animId = `bar${i}_${Math.round(start)}-${Math.round(end)}`;
        return `.bar:nth-of-type(${i + 1}){animation: ${animId} ${duration}s forwards linear}
    @keyframes ${animId}{from{width: ${start}px} to{width: ${end}px}}\n`;
    })
        .join("");
    if (animations.length && currentAnimations !== animations)
        setCurrentAnimations(animations);
    return (react_1.default.createElement("svg", { viewBox: "0 0 100 320", preserveAspectRatio: "none", style: { width: "100%" } },
        react_1.default.createElement("style", null, `.bar{x: 0; height: 34px; opacity: 0.6} @media not all and (prefers-reduced-motion){${currentAnimations}}`),
        react_1.default.createElement("rect", { x: "0", y: "0", width: "100", height: "320", fill: theme.panelOuter }),
        limits.map((l, i) => (react_1.default.createElement("line", { key: `limit${i}`, x2: "100", y1: 40 * i + 40, y2: 40 * i + 40, stroke: l.colour }))),
        react_1.default.createElement("g", null, maxCount
            ? bands.map((b, i) => (react_1.default.createElement("rect", { key: `band${i}`, className: "bar", y: 40 * i + 3, width: endWidths[i], fill: b.colour },
                react_1.default.createElement("title", null, b.title))))
            : null)));
};
exports.FeatureControlLimitChart = react_1.default.memo(exports.Chart);
//# sourceMappingURL=FeatureControlLimitChart.js.map